import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Plus, Trash2, Power, Loader2, Eye, EyeOff, Image as ImageIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { asUTC } from '../../lib/ctTime';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyDraft = {
    key: '',
    eyebrow: 'AN UPDATE HAS BEEN MADE',
    headline: '',
    headline_accent: '',
    subtext: '',
    cta_text: 'Got it',
    footer: '',
    image_url: '',
    link_url: '',
    enabled: true,
    start_at: '',
    expire_at: '',
};

/**
 * AnnouncementsAdminTab
 *
 * Author + broadcast full-screen login banners without a code deploy.
 * The current active announcement (enabled=true, most recent) is served
 * by GET /api/announcements/active and rendered by the player-side
 * AnnouncementBanner component. Only ONE can be enabled at a time —
 * enabling a new one auto-disables the previous one so we don't stack.
 *
 * The `key` field is the localStorage suffix for per-player dismissal —
 * to re-broadcast to everyone who's already dismissed, change the key
 * (or create a new announcement with a fresh key).
 */
export const AnnouncementsAdminTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(emptyDraft);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [quickLink, setQuickLink] = useState('');
    const [quickPosting, setQuickPosting] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const imgSrc = (u) => (u ? (u.startsWith('http') ? u : process.env.REACT_APP_BACKEND_URL + u) : '');

    const uploadImage = async (fileList) => {
        const file = fileList && fileList[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        setUploading(true);
        try {
            const { data } = await axios.post(`${API}/admin/announcements/upload-image`, fd, {
                withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            setDraft(d => ({ ...d, image_url: data.image_url }));
            toast.success('Promo image uploaded');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Image upload failed');
        } finally {
            setUploading(false);
        }
    };

    const quickPost = async (fileList) => {
        const file = fileList && fileList[0];
        if (!file) return;
        setQuickPosting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data: up } = await axios.post(`${API}/admin/announcements/upload-image`, fd, {
                withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            const key = `photo-${Date.now().toString(36)}`;
            await axios.post(`${API}/admin/announcements`, {
                key, headline: '', image_url: up.image_url, link_url: quickLink.trim(), enabled: true,
            }, { withCredentials: true });
            toast.success('Photo is live — broadcasting to everyone now');
            setQuickLink('');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to post photo');
        } finally {
            setQuickPosting(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
            setItems(data.announcements || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load announcements');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!draft.key.trim() || (!draft.headline.trim() && !draft.image_url)) {
            toast.error('Add a key and either a headline or a promo image');
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.post(`${API}/admin/announcements`, draft, { withCredentials: true });
            toast.success(`Announcement '${data.announcement.key}' created${draft.enabled ? ' + broadcasting now' : ''}`);
            setDraft(emptyDraft);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create');
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (item) => {
        try {
            await axios.put(`${API}/admin/announcements/${item.id}`, {
                enabled: !item.enabled,
            }, { withCredentials: true });
            toast.success(item.enabled ? 'Announcement paused' : 'Announcement enabled — everyone else auto-disabled');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update');
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete announcement '${item.key}'? This can't be undone.`)) return;
        try {
            await axios.delete(`${API}/admin/announcements/${item.id}`, { withCredentials: true });
            toast.success('Deleted');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to delete');
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-announcements-tab">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFD700]/15 flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-[#FFD700]" />
                </div>
                <div className="flex-1">
                    <h2 className="text-white font-black text-xl">Login Announcement Banners</h2>
                    <p className="text-[#A0AAB2] text-sm mt-1 max-w-2xl">
                        Craft a big full-screen banner that pops for players once, right after they log in.
                        Only one is broadcast at a time — enabling a new one auto-pauses the previous.
                        Change the <span className="text-[#FFD700] font-mono">key</span> to re-broadcast to
                        players who already dismissed a previous version.
                    </p>
                </div>
            </div>

            {/* Quick photo post — simplest path: pick a photo, it goes live instantly */}
            <div className="rounded-2xl border-2 border-[#FFD700]/40 p-5" data-testid="announcement-quick-post"
                 style={{ background: 'linear-gradient(150deg, #1a1300 0%, #0a0a0a 65%, #001a04 100%)' }}>
                <div className="flex items-center gap-2 mb-1">
                    <ImageIcon className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-white font-black text-base">Quick Photo Post</h3>
                    <span className="text-[10px] font-black uppercase tracking-wider text-black bg-[#39FF14] rounded px-1.5 py-0.5">Easiest</span>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-4">Just pick a photo — it goes live to everyone instantly. No headline or other fields needed.</p>
                <div className="flex flex-wrap items-center gap-3">
                    <label className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-black text-black cursor-pointer transition active:scale-[0.98] ${quickPosting ? 'opacity-60 pointer-events-none' : ''}`}
                        style={{ background: 'linear-gradient(90deg, #FFD700 0%, #39FF14 100%)', boxShadow: '0 8px 28px -8px rgba(57,255,20,0.5)' }}>
                        {quickPosting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                        {quickPosting ? 'Posting…' : 'Choose photo & post'}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                            disabled={quickPosting} onChange={(e) => quickPost(e.target.files)} data-testid="announcement-quick-input" />
                    </label>
                    <Input value={quickLink} onChange={(e) => setQuickLink(e.target.value)}
                        placeholder="Optional link when tapped (e.g. /wallet)"
                        className="bg-black border-white/10 text-white flex-1 min-w-[220px]" data-testid="announcement-quick-link" />
                </div>
                <p className="mt-2 text-[10px] text-[#666]">PNG/JPEG/WEBP/GIF up to 8 MB. Enabling this auto-pauses any other banner. Need text, a headline or a custom button? Use the full editor below.</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-4 space-y-4" data-testid="announcement-editor">
                <div className="flex items-center justify-between">
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">New Announcement</h3>
                    <button
                        type="button"
                        onClick={() => setShowPreview(v => !v)}
                        className="inline-flex items-center gap-1.5 text-xs text-[#A0AAB2] hover:text-white"
                    >
                        {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {showPreview ? 'Hide preview' : 'Show preview'}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label className="text-white text-xs">Key (localStorage suffix, unique)</Label>
                        <Input
                            value={draft.key}
                            onChange={(e) => setDraft(d => ({ ...d, key: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                            placeholder="e.g. payout-3h-v1"
                            className="bg-black border-white/10 text-white font-mono"
                            data-testid="announcement-key"
                        />
                        <p className="text-[10px] text-[#666]">Lowercase, digits, dot, dash, underscore only. Bump this to re-broadcast to everyone.</p>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white text-xs">Eyebrow (small caps above headline)</Label>
                        <Input
                            value={draft.eyebrow}
                            onChange={(e) => setDraft(d => ({ ...d, eyebrow: e.target.value }))}
                            placeholder="AN UPDATE HAS BEEN MADE"
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-eyebrow"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white text-xs">Headline (big text)</Label>
                        <Input
                            value={draft.headline}
                            onChange={(e) => setDraft(d => ({ ...d, headline: e.target.value }))}
                            placeholder="Payments in"
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-headline"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white text-xs">Headline accent (green highlight line, optional)</Label>
                        <Input
                            value={draft.headline_accent}
                            onChange={(e) => setDraft(d => ({ ...d, headline_accent: e.target.value }))}
                            placeholder="3 hours or less"
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-headline-accent"
                        />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <Label className="text-white text-xs">Promo image (optional — shows big at the top of the banner)</Label>
                        <div className="flex items-center gap-3 flex-wrap">
                            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-black text-white text-sm cursor-pointer hover:border-[#FFD700]/50">
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#FFD700]" /> : <ImageIcon className="w-4 h-4 text-[#FFD700]" />}
                                {uploading ? 'Uploading…' : (draft.image_url ? 'Replace image' : 'Upload image')}
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                    className="hidden"
                                    disabled={uploading}
                                    onChange={(e) => uploadImage(e.target.files)}
                                    data-testid="announcement-image-input"
                                />
                            </label>
                            {draft.image_url && (
                                <div className="flex items-center gap-2">
                                    <img src={imgSrc(draft.image_url)} alt="promo preview" className="h-12 rounded border border-white/15 object-contain bg-black" data-testid="announcement-image-thumb" />
                                    <button type="button" onClick={() => setDraft(d => ({ ...d, image_url: '' }))} className="text-xs text-red-400 hover:text-red-200" data-testid="announcement-image-remove">Remove</button>
                                </div>
                            )}
                        </div>
                        <p className="text-[10px] text-[#666]">PNG/JPEG/WEBP/GIF up to 8 MB — perfect for a flyer or promo design.</p>
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <Label className="text-white text-xs">Link when tapped (optional)</Label>
                        <Input
                            value={draft.link_url}
                            onChange={(e) => setDraft(d => ({ ...d, link_url: e.target.value }))}
                            placeholder="/casino/roulette  ·  /wallet  ·  https://example.com/offer"
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-link-input"
                        />
                        <p className="text-[10px] text-[#666]">Where the image / button sends players. Use an in-app path (e.g. <span className="text-[#FFD700]">/casino/roulette</span>, <span className="text-[#FFD700]">/wallet</span>, <span className="text-[#FFD700]">/sportsbook</span>) or a full https:// link (opens a new tab). Leave blank to just close.</p>
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <Label className="text-white text-xs">Subtext (paragraph under the headline)</Label>
                        <Textarea
                            value={draft.subtext}
                            onChange={(e) => setDraft(d => ({ ...d, subtext: e.target.value }))}
                            placeholder="We just cut our cashout time in half. Request a payout — most players get paid in under 3 hours."
                            className="bg-black border-white/10 text-white min-h-[80px]"
                            data-testid="announcement-subtext"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white text-xs">CTA button text</Label>
                        <Input
                            value={draft.cta_text}
                            onChange={(e) => setDraft(d => ({ ...d, cta_text: e.target.value }))}
                            placeholder="Let's go — I'm ready"
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-cta"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white text-xs">Footer (small print under the CTA, optional)</Label>
                        <Input
                            value={draft.footer}
                            onChange={(e) => setDraft(d => ({ ...d, footer: e.target.value }))}
                            placeholder="Payout desk hours: 8 AM – 10 PM CT"
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-footer"
                        />
                    </div>
                    {/* ---- Scheduler (optional) ---- */}
                    <div className="space-y-2">
                        <Label className="text-white text-xs flex items-center gap-1.5">
                            Start at <span className="text-[10px] text-[#A0AAB2]">(optional)</span>
                        </Label>
                        <Input
                            type="datetime-local"
                            value={draft.start_at}
                            onChange={(e) => setDraft(d => ({ ...d, start_at: e.target.value }))}
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-start-at"
                        />
                        <p className="text-[10px] text-[#666]">Leave blank to broadcast immediately.</p>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white text-xs flex items-center gap-1.5">
                            Expire at <span className="text-[10px] text-[#A0AAB2]">(optional)</span>
                        </Label>
                        <Input
                            type="datetime-local"
                            value={draft.expire_at}
                            onChange={(e) => setDraft(d => ({ ...d, expire_at: e.target.value }))}
                            className="bg-black border-white/10 text-white"
                            data-testid="announcement-expire-at"
                        />
                        <p className="text-[10px] text-[#666]">Leave blank to run until you manually pause.</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={draft.enabled}
                            onChange={(e) => setDraft(d => ({ ...d, enabled: e.target.checked }))}
                            className="w-4 h-4 accent-[#39FF14]"
                            data-testid="announcement-enabled"
                        />
                        <span className="text-sm text-white">Broadcast immediately (disables previous)</span>
                    </label>
                    <div className="flex-1" />
                    <Button
                        onClick={create}
                        disabled={saving}
                        className="bg-[#FFD700] hover:bg-[#FFC700] text-black font-black"
                        data-testid="announcement-save"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                        Create Announcement
                    </Button>
                </div>

                {/* Live preview — miniature version of the banner. */}
                {showPreview && (draft.headline || draft.image_url) && (
                    <div
                        className="mt-2 rounded-2xl overflow-hidden border-2 border-[#FFD700]/40 p-6"
                        style={{ background: 'linear-gradient(150deg, #1a1300 0%, #0a0a0a 60%, #001a04 100%)' }}
                        data-testid="announcement-preview"
                    >
                        {draft.image_url && (
                            <img src={imgSrc(draft.image_url)} alt="promo" className="w-full rounded-xl mb-4 max-h-56 object-contain" />
                        )}
                        <p className="text-center text-[9px] font-black tracking-[0.28em] text-[#FFD700] mb-2">
                            {draft.eyebrow || 'AN UPDATE HAS BEEN MADE'}
                        </p>
                        <h1
                            className="text-center font-black leading-[0.95] mb-3"
                            style={{
                                fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
                                background: 'linear-gradient(180deg, #FFFFFF 0%, #FFD700 55%, #39FF14 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {draft.headline}
                            {draft.headline_accent && (
                                <>
                                    <br />
                                    <span style={{ WebkitTextFillColor: '#39FF14' }}>{draft.headline_accent}</span>
                                </>
                            )}
                        </h1>
                        {draft.subtext && (
                            <p className="text-center text-white/85 text-sm mb-3">{draft.subtext}</p>
                        )}
                        <div className="flex justify-center">
                            <div
                                className="px-5 py-2.5 rounded-xl font-black text-black text-sm"
                                style={{ background: 'linear-gradient(90deg, #FFD700 0%, #39FF14 100%)' }}
                            >
                                {draft.cta_text || 'Got it'}
                            </div>
                        </div>
                        {draft.footer && (
                            <p className="mt-3 text-center text-[10px] text-white/50">{draft.footer}</p>
                        )}
                    </div>
                )}
            </div>

            {/* List of existing announcements */}
            <div className="space-y-2">
                <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                    All Announcements ({items.length})
                </h3>
                {loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#FFD700] animate-spin" /></div>
                ) : items.length === 0 ? (
                    <p className="text-[#A0AAB2] text-sm py-4">No announcements yet. Create one above.</p>
                ) : (
                    <div className="space-y-2" data-testid="announcement-list">
                        {items.map((it) => {
                            const now = Date.now();
                            const startTs = it.start_at ? asUTC(it.start_at).getTime() : null;
                            const expireTs = it.expire_at ? asUTC(it.expire_at).getTime() : null;
                            const scheduled = it.enabled && startTs && startTs > now;
                            const expired = it.enabled && expireTs && expireTs <= now;
                            const liveNow = it.enabled && !scheduled && !expired;
                            return (
                            <div
                                key={it.id}
                                className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 ${
                                    liveNow
                                        ? 'bg-[#39FF14]/8 border-[#39FF14]/40'
                                        : scheduled
                                            ? 'bg-[#FFD700]/5 border-[#FFD700]/30'
                                            : 'bg-[#0a0a0a] border-white/10'
                                }`}
                                data-testid={`announcement-row-${it.key}`}
                            >
                                <div className="flex-1 min-w-[240px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-mono text-[#FFD700]">{it.key}</span>
                                        {liveNow && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-[#39FF14] text-black text-[9px] font-black uppercase tracking-wider">
                                                Live
                                            </span>
                                        )}
                                        {scheduled && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-[#FFD700] text-black text-[9px] font-black uppercase tracking-wider">
                                                Scheduled
                                            </span>
                                        )}
                                        {expired && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-[#666] text-white text-[9px] font-black uppercase tracking-wider">
                                                Expired
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-white text-sm font-bold mt-1 truncate">
                                        {it.headline}{it.headline_accent ? ` ${it.headline_accent}` : ''}
                                    </p>
                                    {it.subtext && (
                                        <p className="text-[#A0AAB2] text-xs mt-1 line-clamp-2">{it.subtext}</p>
                                    )}
                                    <div className="text-[10px] text-[#666] mt-1 flex flex-wrap gap-3">
                                        {it.start_at && <span>Starts {asUTC(it.start_at).toLocaleString(undefined, { timeZone: 'America/Chicago' })}</span>}
                                        {it.expire_at && <span>Expires {asUTC(it.expire_at).toLocaleString(undefined, { timeZone: 'America/Chicago' })}</span>}
                                        <span>Updated {it.updated_at ? asUTC(it.updated_at).toLocaleString(undefined, { timeZone: 'America/Chicago' }) : 'never'}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => toggle(it)}
                                        className={`border-white/20 ${it.enabled ? 'text-[#FFB84D] hover:text-white' : 'text-[#39FF14] hover:text-white'}`}
                                        data-testid={`announcement-toggle-${it.key}`}
                                    >
                                        <Power className="w-3.5 h-3.5 mr-1" />
                                        {it.enabled ? 'Pause' : 'Broadcast'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => remove(it)}
                                        className="border-red-500/40 text-red-400 hover:text-red-200"
                                        data-testid={`announcement-delete-${it.key}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnnouncementsAdminTab;
